/**
 * Builds and merges auth-on-file {@link AuthRangeResponse} entries when saving
 * authorisation letter review (review status + optional procedure/basic-info edits).
 *
 * @module authorisationDetails/utils/buildAuthEntries
 */
import { AuthRangeResponse } from "../../../data-model/medicalPaOrdersModel";
import { AuthLetterReviewStatus } from "../../../enums/authLetterReviewStatus";
import { setAuthLetterReviewStatusOnJCodes } from "../../../utils/updateJcodesWithCorrectDocRef";
import {
  BasicInfoFieldState,
  DiagnosisDetailItemState,
  PLACEHOLDER,
  ProcedureFieldState,
} from "./constants";

import {
  resolveNumVisitsFromFormState,
  toStorageDate,
} from "./saveReviewHelpers";

function statusToValidatedFlag(
  status: "valid" | "invalid" | null | undefined,
): boolean | null {
  if (status === "valid") return true;
  if (status === "invalid") return false;
  return null;
}

/** Merges procedure field state into auth_entries for the given jCode ids. */
function applyProcedureFieldStateToAuthEntries(
  authEntries: AuthRangeResponse[],
  procedureFieldStateMap: Record<string, ProcedureFieldState>,
  basicInfoFieldState?: BasicInfoFieldState | null,
): AuthRangeResponse[] {
  if (Object.keys(procedureFieldStateMap).length === 0) return authEntries;
  return authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      const id = jCode.id ?? jCode.j_code ?? "";
      const state = procedureFieldStateMap[id];
      if (!state) return jCode;
      const authLetter = jCode.auth_letter ?? {};
      const numVisitsValue = resolveNumVisitsFromFormState(
        state.numVisits,
        authLetter.calculated_visits,
      );
      return {
        ...jCode,
        j_code: state.cptCode !== PLACEHOLDER ? state.cptCode : jCode.j_code,
        description:
          state.drugName !== PLACEHOLDER
            ? state.drugName
            : (jCode.medicine_name ?? jCode.description),
        medicine_name:
          state.genericName !== PLACEHOLDER || state.drugName !== PLACEHOLDER
            ? [
                state.drugName !== PLACEHOLDER ? state.drugName : "",
                state.genericName !== PLACEHOLDER ? state.genericName : "",
              ]
                .filter(Boolean)
                .join(", ")
            : jCode.medicine_name,
        auth_letter: {
          ...authLetter,
          primary_insurance:
            basicInfoFieldState?.primaryInsurance !== PLACEHOLDER
              ? basicInfoFieldState?.primaryInsurance
              : authLetter.primary_insurance,
          auth_letter_dosage:
            state.dosage !== PLACEHOLDER
              ? state.dosage
              : authLetter.auth_letter_dosage,
          auth_letter_units_approved:
            state.unitsApproved !== PLACEHOLDER
              ? state.unitsApproved
              : authLetter.units_approved,
          units_approved:
            state.unitsApproved !== PLACEHOLDER
              ? state.unitsApproved
              : authLetter.units_approved,
          calculated_visits: numVisitsValue,

          auth_letter_end_date:
            state.endDate !== PLACEHOLDER
              ? toStorageDate(state.endDate)
              : authLetter.auth_letter_end_date
                ? toStorageDate(authLetter.auth_letter_end_date)
                : authLetter.auth_letter_end_date,
          auth_letter_start_date: (() => {
            if (state.startDate !== PLACEHOLDER) {
              return toStorageDate(state.startDate);
            }
            if (basicInfoFieldState?.authInitiatedDate !== PLACEHOLDER) {
              return toStorageDate(
                basicInfoFieldState?.authInitiatedDate ?? "",
              );
            }
            if (authLetter.auth_letter_start_date) {
              return toStorageDate(authLetter.auth_letter_start_date);
            }
            return authLetter.auth_letter_start_date ?? "";
          })(),
          auth_letter_auth_status:
            state.authStatus !== PLACEHOLDER
              ? state.authStatus
              : authLetter.auth_letter_auth_status,
          member_id:
            basicInfoFieldState?.memberId !== PLACEHOLDER
              ? basicInfoFieldState?.memberId
              : (authLetter.member_id ?? ""),
          provider:
            basicInfoFieldState?.providerName !== PLACEHOLDER
              ? basicInfoFieldState?.providerName
              : (authLetter.provider ?? ""),
          auth_letter_auth_number:
            basicInfoFieldState?.authorizationId !== PLACEHOLDER
              ? basicInfoFieldState?.authorizationId
              : (authLetter.auth_letter_auth_number ?? ""),
          source_portal:
            basicInfoFieldState?.sourcePortal !== PLACEHOLDER
              ? basicInfoFieldState?.sourcePortal
              : authLetter.source_portal,
        },
      };
    }),
  }));
}

/** Writes diagnosis code from diagnosis review into `auth_letter.diagnosis_code` (and legacy `auth_letter_icd_code`). */
function applyDiagnosisFieldStateToAuthEntries(
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
  diagnosisDetailFieldState?: DiagnosisDetailItemState[] | null,
): AuthRangeResponse[] {
  if (!diagnosisDetailFieldState?.length) return authEntries;
  const icd = diagnosisDetailFieldState[0]?.diagnosisCode;
  if (!icd || icd === PLACEHOLDER) return authEntries;
  const idSet = new Set(jCodeIds);
  return authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      const id = jCode.id ?? jCode.j_code ?? "";
      if (!idSet.has(id)) return jCode;
      const authLetter = jCode.auth_letter ?? {};
      return {
        ...jCode,
        auth_letter: {
          ...authLetter,
          diagnosis_code: icd,
          auth_letter_icd_code: icd,
        },
      };
    }),
  }));
}

/** Persists tri-state section validation flags at J-code level. */
function applyValidationFlagsToAuthEntries(
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
  basicInfoStatus?: "valid" | "invalid" | null,
  diagnosisDetailStatus?: "valid" | "invalid" | null,
  procedureStatuses?: Record<string, "valid" | "invalid" | null>,
): AuthRangeResponse[] {
  const idSet = new Set(jCodeIds);
  const basicInfoValidated = statusToValidatedFlag(basicInfoStatus);
  const diagnosisValidated = statusToValidatedFlag(diagnosisDetailStatus);

  return authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      const id = jCode.id ?? jCode.j_code ?? "";
      if (!idSet.has(id)) return jCode;
      return {
        ...jCode,
        human_validated: statusToValidatedFlag(procedureStatuses?.[id]),
        basic_info_validated: basicInfoValidated,
        diagnosis_validated: diagnosisValidated,
      };
    }),
  }));
}

/** Marks the targeted J-codes as reviewed and merges any user-edited procedure/basic-info fields. */
export function buildUpdatedAuthEntries(
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
  procedureFieldStateMap?: Record<string, ProcedureFieldState>,
  basicInfoFieldState?: BasicInfoFieldState | null,
  diagnosisDetailFieldState?: DiagnosisDetailItemState[] | null,
  basicInfoStatus?: "valid" | "invalid" | null,
  diagnosisDetailStatus?: "valid" | "invalid" | null,
  procedureStatuses?: Record<string, "valid" | "invalid" | null>,
): AuthRangeResponse[] {
  let updated = setAuthLetterReviewStatusOnJCodes(
    authEntries,
    jCodeIds,
    AuthLetterReviewStatus.ReviewedAndSaved,
  );
  if (
    procedureFieldStateMap &&
    Object.keys(procedureFieldStateMap).length > 0
  ) {
    updated = applyProcedureFieldStateToAuthEntries(
      updated,
      procedureFieldStateMap,
      basicInfoFieldState,
    );
  }
  updated = applyDiagnosisFieldStateToAuthEntries(
    updated,
    jCodeIds,
    diagnosisDetailFieldState,
  );
  updated = applyValidationFlagsToAuthEntries(
    updated,
    jCodeIds,
    basicInfoStatus,
    diagnosisDetailStatus,
    procedureStatuses,
  );
  return updated;
}
